import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Clock, ChevronDown, Check, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEvents } from '@/contexts/EventsContext';
import { useAuth } from '@/contexts/AuthContext';
import { DEFAULT_OPEN_TIME, DEFAULT_CLOSE_TIME } from '@/lib/constants';
import { timeToMinutes, minutesToTime } from '@/lib/time';
import { checkBookingConflict, checkBusyConflict } from '@/lib/bookingValidation';

interface TimePickerProps {
    value: string;
    onChange: (value: string) => void;
    step?: number;
    disabled?: boolean;
    minTime?: string;
    maxTime?: string;
    breakStart?: string | null;
    breakEnd?: string | null;
    className?: string;
    selectedDate?: Date;
    hairdresserId?: string;
    excludeBookingId?: number | null;
    excludeMinTime?: boolean;
    disableBefore?: string;
    serviceDuration?: number;
    closingTime?: string;
}

const TimePicker: React.FC<TimePickerProps> = ({
    value,
    onChange,
    step = 30,
    disabled = false,
    minTime = DEFAULT_OPEN_TIME,
    maxTime = DEFAULT_CLOSE_TIME,
    breakStart = null,
    breakEnd = null,
    className = "",
    selectedDate,
    hairdresserId,
    excludeBookingId,
    excludeMinTime = false,
    disableBefore,
    serviceDuration = 0, // Durata in minuti del servizio selezionato
    closingTime
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const { busySlots, bookings, performances, shiftChanges } = useEvents();
    const { organization } = useAuth();
    const constraintsEnabled = organization?.constraints_enabled !== false;

    // LOGICA DI DISPONIBILITÀ
    const isSlotAvailable = (timeStr: string) => {
        if (!selectedDate || !hairdresserId) return true;

        const slotStart = timeToMinutes(timeStr);
        const slotEnd = slotStart + serviceDuration;
        const dayLimit = timeToMinutes(closingTime || maxTime);

        // --- OVERTIME CHECK ---
        const yyyy = selectedDate.getFullYear();
        const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const dd = String(selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${yyyy}-${mm}-${dd}`;

        const isCoveredByOvertime = (shiftChanges || []).some((sc: any) => {
            if (sc.shift_date !== dateStr) return false;
            if (hairdresserId && sc.hairdresser_id !== hairdresserId) return false;
            const scStartMins = timeToMinutes(sc.start_time.slice(0, 5));
            const scEndMins = timeToMinutes(sc.end_time.slice(0, 5));
            return slotStart >= scStartMins && slotEnd <= scEndMins;
        });

        // 1. Controllo sforamento orario di chiusura (bypassato se straordinario)
        if (!isCoveredByOvertime && slotEnd > dayLimit) return false;

        if (constraintsEnabled) {
            // 2. Controllo incrocio con la PAUSA PRANZO (bypassato se straordinario)
            if (!isCoveredByOvertime && breakStart && breakEnd) {
                const bStart = timeToMinutes(breakStart);
                const bEnd = timeToMinutes(breakEnd);
                if (slotStart < bEnd && slotEnd > bStart) return false;
            }

            // 3. Controllo sovrapposizione Prenotazioni (SEMPRE attivo)
            const hasBookingConflict = checkBookingConflict(
                bookings,
                performances,
                hairdresserId,
                dateStr,
                slotStart,
                slotEnd,
                excludeBookingId || undefined
            );

            if (hasBookingConflict) return false;

            // 4. Controllo PERMESSI/CHIUSURE (bypassato se straordinario)
            if (!isCoveredByOvertime) {
                const slotStartISO = new Date(`${dateStr}T${timeStr}:00`);
                const slotEndISO = new Date(slotStartISO.getTime() + serviceDuration * 60000);
                const targetDayStart = new Date(slotStartISO);
                targetDayStart.setHours(0, 0, 0, 0);

                const hasBusyConflict = checkBusyConflict(
                    busySlots,
                    hairdresserId,
                    targetDayStart,
                    slotStartISO,
                    slotEndISO
                );

                if (hasBusyConflict) return false;
            }
        }

        return true;
    };

    const timeOptions = useMemo(() => {
        const times: string[] = [];
        const startMins = timeToMinutes(minTime) + (excludeMinTime ? step : 0);
        const endMins = timeToMinutes(maxTime);

        for (let m = startMins; m <= endMins; m += step) {
            times.push(minutesToTime(m));
        }
        return times;
    }, [step, minTime, maxTime, excludeMinTime]);

    const filteredOptions = useMemo(() => {
        if (!searchQuery) return timeOptions;
        const q = searchQuery.replace(':', '');
        return timeOptions.filter(t => t.replace(':', '').startsWith(q));
    }, [timeOptions, searchQuery]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen) {
            setSearchQuery("");
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [isOpen]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        let digits = val.replace(/\D/g, '').slice(0, 4);
        let isAutoPrefixed = false;

        // Se l'utente digita da 2 a 9 come primo numero, mettiamo lo 0 davanti
        if (digits.length === 1 && /^[3-9]$/.test(digits)) {
            digits = '0' + digits;
            isAutoPrefixed = true;
        }

        let formatted = digits;
        if (digits.length > 2) {
            formatted = digits.slice(0, 2) + ':' + digits.slice(2);
        } else if (digits.length === 2) {
            if (isAutoPrefixed || (!val.endsWith(':') && val.length > searchQuery.length) || val.endsWith(':')) {
                // Se stiamo scrivendo e arriviamo a 2 cifre, o l'abbiamo forzato noi, aggiungiamo il :
                formatted = digits + ':';
            }
        }

        setSearchQuery(formatted);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            const firstAvailable = filteredOptions.find(t => isSlotAvailable(t));
            if (firstAvailable) {
                onChange(firstAvailable);
                setIsOpen(false);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <div
                className={`w-full h-[48px] flex items-center justify-between px-3 py-2.5 rounded-xl border transition-all outline-none font-medium ${disabled ? 'bg-gray-100 text-gray-400 border-transparent cursor-not-allowed' :
                    isOpen ? 'bg-white border-gray-900 ring-1 ring-gray-900' : 'bg-gray-50 border-gray-200 hover:bg-white'
                    }`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <div className="flex items-center gap-2 truncate flex-1">
                    <Clock size={16} className="text-gray-500" />
                    {isOpen ? (
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyDown={handleKeyDown}
                            placeholder={value || "--:--"}
                            className="bg-transparent border-none outline-none w-full p-0 text-gray-900 placeholder:text-gray-300"
                            onClick={(e) => e.stopPropagation()}
                        />
                    ) : (
                        <span className={!value ? 'text-gray-400' : ''}>{value || "--:--"}</span>
                    )}
                </div>
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }}
                        className="absolute top-full left-0 right-0 mt-1 z-[60] bg-white border border-gray-100 rounded-xl shadow-xl max-h-60 overflow-y-auto custom-scrollbar"
                        ref={listRef}
                    >
                        <div className="p-1">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((t) => {
                                    const isBeforeLimit = disableBefore ? timeToMinutes(t) <= timeToMinutes(disableBefore) : false;
                                    const isUnavailable = !isSlotAvailable(t) || isBeforeLimit;

                                    return (
                                        <button
                                            key={t}
                                            type="button"
                                            disabled={isUnavailable}
                                            onClick={(e) => { e.stopPropagation(); onChange(t); setIsOpen(false); }}
                                            className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-colors ${value === t ? 'bg-gray-50 text-gray-900 font-bold' :
                                                isUnavailable ? 'text-gray-300 cursor-not-allowed opacity-50' : 'text-gray-600 hover:bg-gray-50'
                                                }`}
                                        >
                                            <span>{t}</span>
                                            {value === t && <Check size={14} className="text-gray-900" />}
                                        </button>
                                    );
                                })
                            ) : (
                                <div className="px-3 py-4 text-xs text-gray-400 text-center">
                                    Nessun orario trovato
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {disabled && selectedDate && (
                <div className="flex items-center gap-1 mt-2">
                    <AlertCircle size={12} className="text-red-500 shrink-0" />
                    <span className="text-xs font-medium text-red-600">
                        Nessun orario disponibile
                    </span>
                </div>
            )}
        </div>
    );
};

export default TimePicker;